import Axios from "axios";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { Store } from "../../Store";
import { getError } from "../../utils";
import { api } from "../../Api";

const DEFAULT_LOGO =
  "https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

/** Every Association -> downline transfer, for the admin ledger. */
export default function AssociationTransferScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = { headers: { authorization: `Bearer ${userInfo.token}` } };

  const [q, setQ] = useState("");
  const [rows, setRows] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (query = q, page = 1) => {
      setLoading(true);
      try {
        const { data } = await Axios.get(
          api + `/api/association/admin/transfers?q=${encodeURIComponent(query)}&page=${page}&pageSize=15`,
          auth,
        );
        setRows(data);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="assoc-tx-screen">
      <Helmet>
        <title>Merchant Transfers</title>
      </Helmet>
      <style>{CSS}</style>

      <div className="assoc-tx-hero">
        <div>
          <h3 className="mb-1">
            <i className="fa fa-exchange me-2" aria-hidden="true" /> Merchant Transfers
          </h3>
          <p className="mb-0 small">Every balance a Merchant has sent to their downline.</p>
        </div>
      </div>

      <Card className="assoc-tx-card">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title className="h6 mb-0">{rows.count} transfer(s)</Card.Title>
            <Form.Control
              size="sm"
              style={{ maxWidth: 260 }}
              placeholder="Search sender, receiver or note…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q, 1)}
            />
          </div>

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : rows.rows.length === 0 ? (
            <div className="text-muted small py-3">No merchant transfers yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th>Sent to</th>
                    <th className="text-end">Amount</th>
                    <th>OTP</th>
                    <th>Note</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.rows.map((r) => (
                    <tr key={r._id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img src={r.associationLogo || DEFAULT_LOGO} alt="" className="assoc-tx-avatar" />
                          <div>
                            <div className="fw-semibold">{r.associationName || r.fromUsername}</div>
                            <div className="small text-muted">@{r.fromUsername}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img src={r.toPhoto || DEFAULT_LOGO} alt="" className="assoc-tx-avatar" />
                          <div>
                            <div className="fw-semibold">{r.toName}</div>
                            <div className="small text-muted">@{r.toUsername}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-end fw-semibold">{Number(r.amount).toLocaleString()}</td>
                      <td>
                        {r.otpUsed ? (
                          <Badge bg="success">Verified</Badge>
                        ) : (
                          <Badge bg="secondary">No OTP</Badge>
                        )}
                      </td>
                      <td className="small text-muted">{r.note || "—"}</td>
                      <td className="small text-muted">{fmtDate(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows.pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button size="sm" variant="outline-secondary" disabled={rows.page <= 1} onClick={() => load(q, rows.page - 1)}>
                Prev
              </Button>
              <span className="small align-self-center">Page {rows.page} / {rows.pages}</span>
              <Button size="sm" variant="outline-secondary" disabled={rows.page >= rows.pages} onClick={() => load(q, rows.page + 1)}>
                Next
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

const CSS = `
.assoc-tx-screen { padding: 16px; max-width: 1200px; margin: 0 auto; }
.assoc-tx-hero {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 16px;
}
.assoc-tx-card { border-radius: 14px; border: none; box-shadow: 0 2px 10px rgba(0,0,0,.06); }
.assoc-tx-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #f1f3f5; }
@media (max-width: 576px) {
  .assoc-tx-screen { padding: 10px; }
  .assoc-tx-hero { padding: 16px; }
}
`;
